import { useState } from 'react';
import { DECKS } from '../data/deckConfig';
import { getDeckStats, isDuplicateCard } from '../utils/deckUtils';

const MyCardsModal = ({
  onClose,
  currentDeckId,
  onDeckChange,
  knownChars,
  learningChars,
  isPro,
  isAuthenticated,
  customCards,
  onAddCustomCard,
  onDeleteCustomCard,
  onResetProgress,
  deckData, // The actual deck data for displaying characters
}) => {
  const [activeTab, setActiveTab] = useState('decks'); // 'decks', 'progress', 'custom'
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCard, setNewCard] = useState({ char: '', pinyin: '', meaning: '', example: '' });
  const [addError, setAddError] = useState('');

  const handleAddCard = () => {
    setAddError('');

    // Validation
    if (!newCard.char.trim()) {
      setAddError('Character is required');
      return;
    }
    if (!newCard.pinyin.trim()) {
      setAddError('Pinyin is required');
      return;
    }
    if (!newCard.meaning.trim()) {
      setAddError('Meaning is required');
      return;
    }

    // Check for duplicates in main deck
    if (isDuplicateCard(newCard.char)) {
      setAddError('This character already exists in the Characters deck');
      return;
    }

    // Check for duplicates in custom cards
    if (customCards.some(c => c.char === newCard.char)) {
      setAddError('You already have this card in your custom cards');
      return;
    }

    // Check free user limit
    if (!isPro && customCards.length >= 10) {
      setAddError('Free users can only add up to 10 custom cards. Upgrade to Pro for unlimited!');
      return;
    }

    onAddCustomCard({
      ...newCard,
      id: Date.now(),
      createdAt: new Date().toISOString(),
    });

    setNewCard({ char: '', pinyin: '', meaning: '', example: '' });
    setShowAddForm(false);
  };

  const renderDeckSelector = () => {
    const deckIds = Object.keys(DECKS).filter(id => {
      const config = DECKS[id];
      if (config.requiresAuth && !isAuthenticated) return false;
      if (id === 'custom') return false; // Show custom separately
      return true;
    });

    return (
      <div style={{ padding: '16px' }}>
        <h3 style={{ color: '#f8fafc', fontSize: '16px', marginBottom: '16px' }}>
          Select a Deck
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {deckIds.map(deckId => {
            const config = DECKS[deckId];
            const stats = getDeckStats(deckId, isPro);
            const isActive = currentDeckId === deckId;

            return (
              <button
                key={deckId}
                onClick={() => onDeckChange(deckId)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  background: isActive
                    ? `${config.color}30`
                    : 'rgba(255,255,255,0.05)',
                  border: isActive
                    ? `2px solid ${config.color}`
                    : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                }}
              >
                {/* Icon */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `${config.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  color: config.color,
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}>
                  {config.icon}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px',
                  }}>
                    <span style={{
                      color: '#f8fafc',
                      fontSize: '16px',
                      fontWeight: '600',
                    }}>{config.name}</span>
                    <span style={{
                      color: config.color,
                      fontSize: '12px',
                    }}>{config.nameCN}</span>
                  </div>
                  <div style={{
                    color: '#94a3b8',
                    fontSize: '12px',
                  }}>
                    {stats.available} / {stats.total} cards
                    {!isPro && stats.available < stats.total && (
                      <span style={{ color: '#f59e0b', marginLeft: '8px' }}>
                        🔒 Pro unlocks all
                      </span>
                    )}
                  </div>
                </div>

                {/* Active indicator */}
                {isActive && (
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: config.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                  }}>
                    ✓
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Characters + Custom blend info */}
        {currentDeckId === 'characters' && customCards.length > 0 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(255,215,0,0.1)',
            border: '1px solid rgba(255,215,0,0.3)',
            borderRadius: '12px',
            fontSize: '13px',
            color: '#fcd34d',
          }}>
            ✨ Your {customCards.length} custom card{customCards.length > 1 ? 's are' : ' is'} blended into this deck
          </div>
        )}
      </div>
    );
  };

  const renderProgress = () => {
    const config = DECKS[currentDeckId];
    if (!config) return null;

    return (
      <div style={{ padding: '16px' }}>
        {/* Summary */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
        }}>
          <div style={{
            flex: 1,
            background: 'rgba(34,197,94,0.1)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#22c55e', fontSize: '32px', fontWeight: 'bold' }}>
              {knownChars.size}
            </div>
            <div style={{ color: '#86efac', fontSize: '14px' }}>Known</div>
          </div>
          <div style={{
            flex: 1,
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#ef4444', fontSize: '32px', fontWeight: 'bold' }}>
              {learningChars.size}
            </div>
            <div style={{ color: '#fca5a5', fontSize: '14px' }}>Learning</div>
          </div>
        </div>

        {/* Known Characters */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#22c55e', fontSize: '16px', marginBottom: '12px' }}>
            Known ({knownChars.size})
          </h3>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            maxHeight: '120px',
            overflow: 'auto',
            padding: '4px',
          }}>
            {knownChars.size === 0 ? (
              <span style={{ color: '#64748b', fontSize: '14px' }}>
                No cards yet. Start swiping!
              </span>
            ) : (
              [...knownChars].sort((a, b) => a - b).slice(0, 50).map(idx => {
                const card = deckData?.[idx];
                return card ? (
                  <div
                    key={idx}
                    style={{
                      background: 'rgba(34,197,94,0.2)',
                      border: '1px solid rgba(34,197,94,0.4)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#f8fafc',
                      fontSize: '20px',
                    }}
                    title={`${card.pinyin} - ${card.meaning}`}
                  >
                    {card.char}
                  </div>
                ) : null;
              })
            )}
            {knownChars.size > 50 && (
              <div style={{ color: '#64748b', fontSize: '14px', alignSelf: 'center' }}>
                +{knownChars.size - 50} more
              </div>
            )}
          </div>
        </div>

        {/* Learning Characters */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#ef4444', fontSize: '16px', marginBottom: '12px' }}>
            Learning ({learningChars.size})
          </h3>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            maxHeight: '120px',
            overflow: 'auto',
            padding: '4px',
          }}>
            {learningChars.size === 0 ? (
              <span style={{ color: '#64748b', fontSize: '14px' }}>
                No cards in learning list.
              </span>
            ) : (
              [...learningChars].sort((a, b) => a - b).slice(0, 50).map(idx => {
                const card = deckData?.[idx];
                return card ? (
                  <div
                    key={idx}
                    style={{
                      background: 'rgba(239,68,68,0.2)',
                      border: '1px solid rgba(239,68,68,0.4)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#f8fafc',
                      fontSize: '20px',
                    }}
                    title={`${card.pinyin} - ${card.meaning}`}
                  >
                    {card.char}
                  </div>
                ) : null;
              })
            )}
            {learningChars.size > 50 && (
              <div style={{ color: '#64748b', fontSize: '14px', alignSelf: 'center' }}>
                +{learningChars.size - 50} more
              </div>
            )}
          </div>
        </div>

        {/* Reset Button */}
        {(knownChars.size > 0 || learningChars.size > 0) && (
          <button
            onClick={onResetProgress}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid rgba(239,68,68,0.5)',
              background: 'rgba(239,68,68,0.1)',
              color: '#ef4444',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Reset Progress for {config.name}
          </button>
        )}
      </div>
    );
  };

  const renderCustomCards = () => {
    if (!isAuthenticated) {
      return (
        <div style={{
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
          <h3 style={{ color: '#f8fafc', marginBottom: '8px' }}>Sign in Required</h3>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>
            Create an account to add your own custom cards.
          </p>
        </div>
      );
    }

    const canAddMore = isPro || customCards.length < 10;

    return (
      <div style={{ padding: '16px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <div>
            <h3 style={{ color: '#fcd34d', fontSize: '16px', margin: 0 }}>
              Custom Cards ({customCards.length}{!isPro && '/10'})
            </h3>
            {!isPro && (
              <p style={{ color: '#94a3b8', fontSize: '12px', margin: '4px 0 0' }}>
                Free users: max 10 cards
              </p>
            )}
          </div>
          {canAddMore && !showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #ffd700, #f59e0b)',
                border: 'none',
                borderRadius: '8px',
                color: '#713f12',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              + Add Card
            </button>
          )}
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div style={{
            background: 'rgba(255,215,0,0.1)',
            border: '1px solid rgba(255,215,0,0.3)',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '16px',
          }}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#fcd34d', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Character *
              </label>
              <input
                type="text"
                value={newCard.char}
                onChange={(e) => setNewCard({ ...newCard, char: e.target.value })}
                placeholder="汉"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,215,0,0.3)',
                  borderRadius: '8px',
                  color: '#f8fafc',
                  fontSize: '18px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#fcd34d', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Pinyin *
              </label>
              <input
                type="text"
                value={newCard.pinyin}
                onChange={(e) => setNewCard({ ...newCard, pinyin: e.target.value })}
                placeholder="hàn"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,215,0,0.3)',
                  borderRadius: '8px',
                  color: '#f8fafc',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: '#fcd34d', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Meaning *
              </label>
              <input
                type="text"
                value={newCard.meaning}
                onChange={(e) => setNewCard({ ...newCard, meaning: e.target.value })}
                placeholder="Chinese; Han"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,215,0,0.3)',
                  borderRadius: '8px',
                  color: '#f8fafc',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#fcd34d', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Example (optional)
              </label>
              <input
                type="text"
                value={newCard.example}
                onChange={(e) => setNewCard({ ...newCard, example: e.target.value })}
                placeholder="汉字 (hànzì) - Chinese characters"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,215,0,0.3)',
                  borderRadius: '8px',
                  color: '#f8fafc',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {addError && (
              <div style={{
                color: '#ef4444',
                fontSize: '13px',
                marginBottom: '12px',
                padding: '8px 12px',
                background: 'rgba(239,68,68,0.1)',
                borderRadius: '8px',
              }}>
                {addError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewCard({ char: '', pinyin: '', meaning: '', example: '' });
                  setAddError('');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#94a3b8',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddCard}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'linear-gradient(135deg, #ffd700, #f59e0b)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#713f12',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Add Card
              </button>
            </div>
          </div>
        )}

        {/* Custom Cards List */}
        {customCards.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '32px',
            color: '#64748b',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✨</div>
            <p>No custom cards yet.</p>
            <p style={{ fontSize: '13px' }}>
              Add your own characters to study!
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {customCards.map((card) => (
              <div
                key={card.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'rgba(255,215,0,0.1)',
                  border: '1px solid rgba(255,215,0,0.2)',
                  borderRadius: '12px',
                }}
              >
                <div style={{
                  fontSize: '28px',
                  color: '#fcd34d',
                  width: '48px',
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                  {card.char}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: '#f8fafc',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}>
                    {card.pinyin}
                  </div>
                  <div style={{
                    color: '#94a3b8',
                    fontSize: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {card.meaning}
                  </div>
                </div>
                <button
                  onClick={() => onDeleteCustomCard(card.id)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    border: '1px solid rgba(239,68,68,0.3)',
                    background: 'rgba(239,68,68,0.1)',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '16px',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Info about blending */}
        {customCards.length > 0 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(59,130,246,0.1)',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: '12px',
            fontSize: '13px',
            color: '#93c5fd',
          }}>
            💡 Custom cards are automatically blended into the Characters deck
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: 'decks', label: 'Decks', icon: '📚' },
    { id: 'progress', label: 'Progress', icon: '📊' },
    { id: 'custom', label: 'Custom', icon: '✨' },
  ];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1e293b, #0f172a)',
        borderRadius: '20px',
        width: '100%',
        maxWidth: 'min(500px, calc(100vw - 32px))',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(255,255,255,0.1)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
        }}>
          <h2 style={{ color: '#f8fafc', margin: 0, fontSize: '20px' }}>My Cards</h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: '#94a3b8',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '18px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '12px 8px',
                background: activeTab === tab.id
                  ? 'rgba(59,130,246,0.2)'
                  : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id
                  ? '2px solid #3b82f6'
                  : '2px solid transparent',
                color: activeTab === tab.id ? '#f8fafc' : '#64748b',
                cursor: 'pointer',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
        }}>
          {activeTab === 'decks' && renderDeckSelector()}
          {activeTab === 'progress' && renderProgress()}
          {activeTab === 'custom' && renderCustomCards()}
        </div>
      </div>
    </div>
  );
};

export default MyCardsModal;
